import { reactive } from 'vue'
import { courseDetail } from '@/api/englishLearning/course'
import {
  courseSupportsLearningPlan,
  normalizeTypeList
} from '@/utils/courseDetailNav'

const state = reactive({
  visible: false,
  courseTitle: '',
  wordCount: 0,
  sentenceCount: 0,
  typeList: [],
  allowSkip: true,
  defaultDailyLearn: 20,
  defaultDailyReview: 20,
  _resolve: null
})

export function getStudyPlanGateState () {
  return state
}

function finish (payload) {
  state.visible = false
  const resolve = state._resolve
  state._resolve = null
  resolve?.(payload)
}

/** 确认设置计划 */
export function confirmStudyPlanGate (plan) {
  finish({ action: 'confirm', plan: plan || null })
}

/** 跳过计划（仍继续加入课程） */
export function skipStudyPlanGate () {
  finish({ action: 'skip', plan: null })
}

/** 取消整次加入 */
export function cancelStudyPlanGate () {
  finish({ action: 'cancel', plan: null })
}

/**
 * 打开设置学习计划弹窗（不负责 collectAdd / courseAdd）
 * @returns {Promise<{ action: 'confirm'|'skip'|'cancel', plan: object|null }>}
 */
export function openStudyPlanGate (options = {}) {
  const {
    title = '',
    wordCount = 0,
    sentenceCount = 0,
    typeList = [],
    allowSkip = true,
    defaultDailyLearn = 20,
    defaultDailyReview = 20
  } = options

  return new Promise((resolve) => {
    if (state._resolve) {
      state._resolve({ action: 'cancel', plan: null })
      state._resolve = null
    }
    state.courseTitle = title || ''
    state.wordCount = Number(wordCount) || 0
    state.sentenceCount = Number(sentenceCount) || 0
    state.typeList = normalizeTypeList(typeList)
    state.allowSkip = allowSkip !== false
    state.defaultDailyLearn = Number(defaultDailyLearn) || 20
    state.defaultDailyReview = Number(defaultDailyReview) || 0
    state._resolve = resolve
    state.visible = true
  })
}

/**
 * 加入课程前弹出学习计划（可跳过）
 * - cancel：用户关闭/取消，调用方不应加入
 * - skip：跳过计划，调用方应加入且 plan=null（含仅句子/无单词，不弹窗）
 * - unsupported：不可建计划且 allowSkip=false（如更换首页计划课）
 * - confirm：已设计划，调用方应加入并带上 plan
 *
 * @param {object} courseLike { id, title, word_count, count, type_list }
 * @param {{ allowSkip?: boolean, fetchDetail?: boolean }} [opts]
 * @returns {Promise<{ action: 'confirm'|'skip'|'cancel'|'unsupported', plan: object|null }>}
 */
export async function promptStudyPlanBeforeAdd (courseLike = {}, opts = {}) {
  const allowSkip = opts.allowSkip !== false
  const fetchDetail = opts.fetchDetail !== false
  let title = courseLike.title || ''
  let wordCount = Number(courseLike.word_count) || 0
  let sentenceCount = Number(courseLike.count) || 0
  let typeList = normalizeTypeList(courseLike.type_list)

  if (fetchDetail && courseLike.id) {
    try {
      const res = await courseDetail({ id: courseLike.id })
      if (res?.code === 1 && res.data) {
        const d = res.data
        title = d.title || title
        wordCount = Number(d.word_count) || wordCount
        sentenceCount = Number(d.count) || sentenceCount
        typeList = normalizeTypeList(d.type_list || typeList)
      }
    } catch (e) {
      console.error('获取课程详情失败，使用列表数量设置学习计划', e)
    }
  }

  // 仅句子 / 无可学单词：不弹计划、不调 plan_save（可跳过场景仍允许加入课程）
  if (!courseSupportsLearningPlan({ type_list: typeList, word_count: wordCount })) {
    if (!allowSkip) return { action: 'unsupported', plan: null }
    return { action: 'skip', plan: null }
  }

  return openStudyPlanGate({
    title,
    wordCount,
    sentenceCount,
    typeList,
    allowSkip
  })
}
